import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogoMark } from "@/components/logo";
import { signIn } from "@/lib/auth-client";
import { safeNextPath } from "@/lib/next-path";

// Passwordless sign-in: enter an email, receive a magic link. Signing in and
// creating an account are the same calm action — "only an email."
export default function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Where to land afterwards: the page they were trying to reach, or the
  // destination an email link carried. Falls back to the home screen.
  const next = safeNextPath(params.get("next"));
  // A one-tap link from an email that expired, was already used, or was
  // opened by a mail scanner before they got to it.
  const linkExpired = params.get("link") === "expired" || !!params.get("error");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle"
  );
  const [error, setError] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("sending");
    setError("");
    const { error } = await signIn.magicLink({
      email: email.trim(),
      callbackURL: next,
    });
    if (error) {
      setStatus("error");
      setError("We couldn't send the link just now. Please try again.");
    } else {
      setStatus("sent");
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col bg-background px-6">
      <div className="pt-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
      </div>

      {status === "sent" ? (
        <div className="flex flex-1 animate-fade-rise flex-col items-center justify-center gap-5 pb-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MailCheck className="h-8 w-8" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Check your inbox</h1>
            <p className="mx-auto mt-2 max-w-xs text-[15px] leading-relaxed text-muted-foreground">
              We sent a sign-in link to{" "}
              <span className="font-medium text-foreground">{email}</span>. Open
              it on this device to continue.
            </p>
          </div>
          <Button
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => setStatus("idle")}
          >
            Use a different email
          </Button>
          <p className="text-xs text-muted-foreground">
            The link expires shortly. One day at a time.
          </p>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center pb-16">
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <LogoMark size={48} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Sign in to Happy24
              </h1>
              <p className="mt-1 text-[15px] text-muted-foreground">
                Enter your email and we'll send a sign-in link. No password.
              </p>
            </div>
          </div>

          {linkExpired && (
            <div className="mb-5 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 text-center">
              <p className="text-[15px] leading-relaxed text-foreground">
                That link has already been used, or it expired.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Enter your email below and we'll send a fresh one. Nothing was
                lost.
              </p>
            </div>
          )}

          <form onSubmit={onSubmit} className="flex w-full flex-col gap-3">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email address"
            />
            <Button
              type="submit"
              size="xl"
              fullWidth
              disabled={status === "sending" || !email.trim()}
            >
              {status === "sending" ? "Sending…" : "Send me a sign-in link"}
            </Button>
            {status === "error" && (
              <p className="text-center text-sm text-status-red-text" role="alert">
                {error}
              </p>
            )}
          </form>

          <p className="mt-6 max-w-xs text-center text-xs leading-relaxed text-muted-foreground">
            We only ever ask for an email — no name, no phone. Your entries are
            private and yours.
          </p>
        </div>
      )}
    </main>
  );
}
